import React from 'react';

import CircleIcon from '@mui/icons-material/Circle';
import { Box, Link, Typography } from '@sunwisesoftware/sunwise-ui';
import { isEmpty, orderBy } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { useBreakpoint } from 'common/hooks';
import { formatDate, parseDate } from 'common/utils/dates';
import {
    arraytoDictionary,
    getProjectStatusOptions,
} from 'common/utils/helpers';

import ActionsFormatter from './ActionsFormatter';
import CustomStatusEditField from './CustomStatusEditField';

const ProductColumns = ({
    filterData,
    handleClickBulkItems,
    hiddenColumns,
    orders,
    rowModesModel,
    selectedRowId,
    setRowModesModel,
    setSelectedRowId,
}) => {
    const { t } = useTranslation();
    const breakpoint = useBreakpoint();

    const nameColumn = [
        {
            align: 'left',
            display: 'flex',
            field: 'name',
            flex: 1,
            headerName: t('Name'),
            hide: hiddenColumns.includes('name'),
            minWidth: 180,
            order: orders?.name,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Link
                        component={RouterLink}
                        to={`/contacts/${row?.contact?.id}/${row.id}`}
                        sx={{ color: '#000' }}
                    >
                        <Typography fontWeight="bold" variant="caption">
                            {row.name}
                        </Typography>
                    </Link>
                );
            },
        },
    ];

    if (['xs', 'sm'].includes(breakpoint)) {
        return nameColumn;
    }

    const columns = [
        ...nameColumn,
        {
            align: 'left',
            display: 'flex',
            field: 'contact',
            flex: 1,
            headerName: t('Contact'),
            hide: hiddenColumns.includes('contact'),
            minWidth: 250,
            order: orders?.contact,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography variant="caption">
                        {row?.contact?.name || 'S/N'}
                    </Typography>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'status',
            flex: 1,
            headerName: t('Status'),
            hide: hiddenColumns.includes('status'),
            minWidth: 250,
            order: orders?.status,
            renderCell: (params) => {
                const { row } = params;
                const statusDictionary = arraytoDictionary(
                    getProjectStatusOptions(),
                    'value',
                );
                return (
                    <Box alignItems="center" display="flex" gap={1}>
                        <CircleIcon
                            sx={{
                                color: statusDictionary[row?.status]
                                    ?.primary_color,
                                fontSize: 20,
                            }}
                        />
                        <Typography fontWeight="bold" variant="caption">
                            {statusDictionary[row?.status]?.label}
                        </Typography>
                    </Box>
                );
            },
            renderEditCell: (params) => {
                return <CustomStatusEditField {...params} />;
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'proposals',
            flex: 1,
            headerName: t('Proposal', { count: 2 }),
            hide:
                !isEmpty(rowModesModel) || hiddenColumns.includes('proposals'),
            minWidth: 180,
            order: orders?.proposals,
            sortable: false,
            valueGetter: (_, row) => row?.proposals,
        },
        {
            align: 'left',
            display: 'flex',
            field: 'country',
            flex: 1,
            headerName: t('Country'),
            hide: !isEmpty(rowModesModel) || hiddenColumns.includes('country'),
            minWidth: 180,
            order: orders?.country,
        },
        {
            align: 'left',
            display: 'flex',
            field: 'state',
            flex: 1,
            headerName: t('State'),
            hide: !isEmpty(rowModesModel) || hiddenColumns.includes('state'),
            minWidth: 180,
            order: orders?.state,
        },
        {
            align: 'left',
            display: 'flex',
            field: 'created_at',
            flex: 1,
            headerName: t('Created at'),
            hide:
                !isEmpty(rowModesModel) || hiddenColumns.includes('created_at'),
            minWidth: 180,
            order: orders?.created_at,
            renderCell: (params) => {
                const { row } = params;
                if (row?.created_at) {
                    const createdAt = formatDate(
                        parseDate(row?.created_at, 'dd/MM/yyyy HH:mm:ss'),
                        'MM/dd/yyyy HH:mm:ss',
                    );
                    return (
                        <Typography variant="caption">{createdAt}</Typography>
                    );
                }
                return null;
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: '',
            flex: 1,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <ActionsFormatter
                        filterData={filterData}
                        handleClickBulkItems={handleClickBulkItems}
                        row={row}
                        rowModesModel={rowModesModel}
                        selectedRowId={selectedRowId}
                        setRowModesModel={setRowModesModel}
                        setSelectedRowId={setSelectedRowId}
                    />
                );
            },
            sortable: false,
        },
    ];

    return orderBy(columns, ['order'], ['asc']);
};

ProductColumns.propTypes = {
    filterData: PropTypes.object,
    handleClickBulkItems: PropTypes.func,
    hiddenColumns: PropTypes.array,
    orders: PropTypes.object,
    rowModesModel: PropTypes.object,
    selectedRowId: PropTypes.func,
    setRowModesModel: PropTypes.func,
    setSelectedRowId: PropTypes.func,
};

export default ProductColumns;
